import React, { memo } from 'react';
import styled from 'styled-components';

import { validateAddress } from '@front/common/src/forms/validators/address';
import { getCurrentCountry } from '@front/common/src/utils/country';

import useGetPrices from '@front/customer/src/hooks/use-get-prices';
import { CONFIG_BY_COUNTRY } from '@front/customer/src/constants/country-config';
import { CommonCleaningBasketProps } from '@front/customer/src/components/basket/types';

import Separator from '@front/web-ui/src/components/separator/separator';

import NoContentYet from '../no-content-yet';
import Address from '../address';
import ProSection from '../pro-section';
import { StyledBasketContainer } from '../common';
import { useIsITCSelectedOrSuccessfullyPaired } from '../../../hooks/use-is-itc-selected-or-successfully-paired';

import MultipleCleaningAppointments from './multiple/multiple-cleaning-appointments';
import MultiplePrestationsPrices from './multiple/multiple-prestations-prices';
import MembershipCard from './membership-card/membership-card';
import SingleCleaningAppointments from './single/single-cleaning-appointments';
import SinglePrestationsPrices from './single/single-prestations-prices';
import TaxCreditAlert from './common/tax-credit-alert';

export const BASKET_CLEANING_PRICES_LOADED_TEST_ID = 'basket-cleaning-prices-loaded-test-id';

const MembershipCardSeparator = styled(Separator)`
  margin-bottom: 20px;
`;

export type CleaningBasketProps = CommonCleaningBasketProps & {
  pathname: string;
  setIsMembershipRequested: (isRequested: boolean) => void;
  openMembershipPaywall: () => void;
};

const CleaningBasket = ({
  address,
  addressDetails,
  appointment,
  appointmentDate,
  canSubscribeToMembership,
  children,
  cleaningPrestation,
  cleaningPrestationOptions,
  duration,
  hasFreeParking,
  hasMembership,
  hasPets,
  mode,
  openMembershipPaywall,
  pathname,
  proChoices,
  promotion,
  setAddressDetails,
  setIsMembershipRequested,
  shouldShowMembershipCard,
  shouldShowPromoCode,
  shouldUpdateAllOccurrences,
  toggleHasFreeParking,
  universeReference,
  user,
  weeklyRecurrence,
  weeklyRecurrences,
}: CleaningBasketProps) => {
  const { isTaxInfoDisplayed } = CONFIG_BY_COUNTRY[getCurrentCountry()];
  const { prices, pricesError } = useGetPrices();
  const isMultiple = !!cleaningPrestation?.is_recurrent && (mode === 'creation' || shouldUpdateAllOccurrences);
  const userPrices = hasMembership ? prices?.member : prices?.standard;
  const { isITCSelectedOrSuccessfullyPaired, shouldShowTaxCreditPriceInfo } = useIsITCSelectedOrSuccessfullyPaired();

  const prestationPricesCommonProps = {
    cleaningPrestation,
    cleaningPrestationOptions,
    duration,
    hasPets,
    isITCSelectedOrSuccessfullyPaired,
    shouldShowTaxCreditPriceInfo,
    isLoadingPrices: !prices || !!pricesError,
    mode,
    prices: userPrices,
    promotion,
    shouldShowPromoCode,
    universeReference,
    user,
  };

  return validateAddress(address) ? (
    <StyledBasketContainer>
      {!!prices && <span data-testid={BASKET_CLEANING_PRICES_LOADED_TEST_ID} />}
      <Address
        address={address}
        addressDetails={addressDetails}
        setAddressDetails={setAddressDetails}
        hasFreeParking={hasFreeParking}
        toggleHasFreeParking={toggleHasFreeParking}
        hasTopSeparator={false}
      />
      {isMultiple ? (
        <React.Fragment key='multiple'>
          <MultiplePrestationsPrices
            {...prestationPricesCommonProps}
            weeklyRecurrence={weeklyRecurrence}
            weeklyRecurrences={weeklyRecurrences}
          />
          <MultipleCleaningAppointments
            appointmentDate={appointmentDate}
            appointment={appointment}
            weeklyRecurrence={weeklyRecurrence}
          />
        </React.Fragment>
      ) : (
        <React.Fragment key='single'>
          <SinglePrestationsPrices {...prestationPricesCommonProps} />
          <SingleCleaningAppointments appointmentDate={appointmentDate} appointment={appointment} />
        </React.Fragment>
      )}
      {mode === 'creation' && (
        <ProSection proChoices={proChoices} universeRef={universeReference} shouldHideEligiblePros={true} />
      )}
      {shouldShowMembershipCard && (
        <>
          <MembershipCardSeparator />
          <MembershipCard
            isMembershipRequested={hasMembership}
            setIsMembershipRequested={setIsMembershipRequested}
            openMembershipPaywall={openMembershipPaywall}
            pathname={pathname}
            universeReference={universeReference}
            canSubscribeToMembership={canSubscribeToMembership}
            duration={duration}
            cleaningPrestation={cleaningPrestation}
            user={user}
          />
        </>
      )}
      {isTaxInfoDisplayed && shouldShowTaxCreditPriceInfo && <TaxCreditAlert />}
      {children}
    </StyledBasketContainer>
  ) : (
    <div data-testid={BASKET_CLEANING_PRICES_LOADED_TEST_ID}>
      <NoContentYet user={user} universe={universeReference} />
    </div>
  );
};

export default memo(CleaningBasket);
